import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import type { SearchResult, SearchResultType } from "@victor/api-client";
import { api } from "../../lib/api";

/**
 * GlobalSearch · barre de recherche cross-tables dans la topbar (T2.8).
 *
 * - Debounce 300ms après la frappe
 * - Min 2 caractères pour déclencher la requête
 * - Dropdown popover avec résultats groupés par type
 * - Échap ferme · Click outside ferme · Click résultat navigue
 * - Cmd/Ctrl + K : focus
 */
export function GlobalSearch() {
  const [value, setValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounce
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value.trim()), 300);
    return () => window.clearTimeout(t);
  }, [value]);

  // Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const enabled = debounced.length >= 2;
  const query = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.search.global(debounced),
    enabled,
    staleTime: 30_000,
  });

  function handleResultClick(r: SearchResult) {
    setOpen(false);
    setValue("");
    navigate(routeFor(r));
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-navy-400"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher un agent, un site, un document…"
        className="w-full rounded-full bg-navy-50 px-4 py-2 pl-9 pr-14 text-sm text-navy-800 ring-1 ring-navy-100 placeholder:text-navy-400 focus:outline-none focus:ring-2 focus:ring-accent-500"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded border border-navy-200 bg-white px-1.5 py-0.5 text-[10px] font-bold text-navy-500 sm:inline-flex">
        ⌘K
      </span>

      <AnimatePresence>
        {open && enabled ? (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl bg-white p-2 shadow-2xl shadow-navy-800/15 ring-1 ring-navy-100"
            role="listbox"
          >
            {query.isLoading ? (
              <div className="px-3 py-4 text-sm text-navy-500">
                Recherche…
              </div>
            ) : query.error ? (
              <div className="px-3 py-4 text-sm text-red-600">
                Recherche indisponible : {(query.error as Error).message}
              </div>
            ) : (query.data?.results.length ?? 0) === 0 ? (
              <div className="px-3 py-4 text-sm text-navy-500">
                Aucun résultat pour « {debounced} ».
              </div>
            ) : (
              <ResultsList
                results={query.data!.results}
                onClick={handleResultClick}
              />
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function ResultsList({
  results,
  onClick,
}: {
  results: SearchResult[];
  onClick: (r: SearchResult) => void;
}) {
  const groups = groupByType(results);
  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <div key={g.type}>
          <p className="px-3 pt-2 text-[11px] font-bold uppercase tracking-wider text-navy-500">
            {labelForType(g.type)}
          </p>
          <ul>
            {g.items.map((r) => (
              <li key={`${r.type}-${r.id}`}>
                <button
                  type="button"
                  onClick={() => onClick(r)}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent-50"
                  role="option"
                >
                  <TypeIcon type={r.type} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-navy-800">
                      {r.label}
                    </span>
                    {r.snippet ? (
                      <span className="mt-0.5 block truncate text-xs text-navy-500">
                        {r.snippet}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TypeIcon({ type }: { type: SearchResultType }) {
  const common =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg";
  if (type === "etablissement") {
    return (
      <span className={`${common} bg-accent-50 text-accent-600`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
        </svg>
      </span>
    );
  }
  if (type === "user") {
    return (
      <span className={`${common} bg-emerald-50 text-emerald-600`}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </span>
    );
  }
  return (
    <span className={`${common} bg-amber-50 text-amber-600`}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    </span>
  );
}

function labelForType(t: SearchResultType): string {
  if (t === "etablissement") return "Établissements";
  if (t === "user") return "Utilisateurs";
  return "Documents";
}

function routeFor(r: SearchResult): string {
  // V0 : navigation vers la page liste correspondante. On garde l'id en query
  // pour permettre un focus/highlight futur quand les pages détail existeront.
  if (r.type === "etablissement") return `/etablissements?focus=${r.id}`;
  if (r.type === "user") return `/utilisateurs?focus=${r.id}`;
  return `/documents?focus=${r.id}`;
}

function groupByType(results: SearchResult[]) {
  const order: SearchResultType[] = [
    "client",
    "agent",
    "site",
    "devis",
    "facture",
    "opportunite",
    "sous_traitant",
    "etablissement",
    "user",
    "document",
  ];
  const groups: Record<SearchResultType, SearchResult[]> = {
    etablissement: [],
    user: [],
    document: [],
    client: [],
    agent: [],
    site: [],
    devis: [],
    facture: [],
    opportunite: [],
    sous_traitant: [],
  };
  for (const r of results) groups[r.type].push(r);
  return order
    .map((type) => ({ type, items: groups[type] }))
    .filter((g) => g.items.length > 0);
}
